import React, { useState, useRef, useEffect } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Polyline,
  useMap,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { getPreciseDistance } from "geolib";
import L from "leaflet";
import "../styles/TapeMeasurement.css";

// Fix Leaflet default icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

const RecenterMap = ({ position }) => {
  const map = useMap();
  useEffect(() => {
    if (position) {
      map.setView(position, map.getZoom());
    }
  }, [position, map]);
  return null;
};

const TapeMeasurement = () => {
  const [positions, setPositions] = useState([]);
  const [distance, setDistance] = useState(0);
  const [tracking, setTracking] = useState(false);
  const [useMetric, setUseMetric] = useState(true);
  const watchIdRef = useRef(null);
  const originRef = useRef(null);

  const startMeasurement = () => {
    setPositions([]);
    setDistance(0);
    setTracking(true);
    originRef.current = null;

    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        const { latitude, longitude, accuracy } = pos.coords;
        const newPoint = { lat: latitude, lng: longitude };

        if (!originRef.current && accuracy <= 20) {
          originRef.current = newPoint;
          setPositions([newPoint, newPoint]); // Initial pin drop
          return;
        }

        if (originRef.current && accuracy <= 20) {
          const straightDist = getPreciseDistance(originRef.current, newPoint);
          setPositions([originRef.current, newPoint]); // Always straight line
          setDistance(straightDist / 100); // Convert cm to m
        }
      },
      (err) => {
        console.error("GPS error:", err);
        alert("GPS error: " + err.message);
      },
      { enableHighAccuracy: true, maximumAge: 0, timeout: 20000 }
    );
  };

  const stopMeasurement = () => {
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    setTracking(false);
  };

  const reset = () => {
    stopMeasurement();
    originRef.current = null;
    setPositions([]);
    setDistance(0);
  };

  const renderDistance = () => {
    const val = useMetric
      ? `${distance.toFixed(2)} m`
      : `${(distance * 3.28084).toFixed(2)} ft`;
    return `Distance: ${val}`;
  };

  return (
    <div>
      <h3>GPS Measuring Tape</h3>
      <div className="gps-tool-container">
        <div className="controls">
          <button onClick={startMeasurement} disabled={tracking}>
            Start
          </button>
          <button onClick={stopMeasurement} disabled={!tracking}>
            Stop
          </button>
          <button onClick={reset}>Reset</button>
          <button onClick={() => setUseMetric((prev) => !prev)}>
            Switch to {useMetric ? "Feet" : "Meters"}
          </button>
          <div className="distance-display">{renderDistance()}</div>
        </div>

        <MapContainer
          center={positions[0] || [6.5244, 3.3792]}
          zoom={20}
          scrollWheelZoom={true}
          style={{ height: "400px", width: "100%", marginTop: "10px" }}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution=""
          />
          <RecenterMap position={positions[positions.length - 1]} />

          {positions.length > 0 && <Marker position={positions[0]} />}
          {positions.length === 2 && (
            <>
              <Polyline positions={positions.map((p) => [p.lat, p.lng])} color="blue" />
              <Marker position={positions[1]} />
            </>
          )}
        </MapContainer>
      </div>
    </div>
  );
};

export default TapeMeasurement;
